import { ConciliationCaseRelatedItem } from "@/@types/conciliation-history";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { formatDate } from "@/utils/format-date";

import { formatAmount } from "../../utils/amount-display";
import { CASE_TIMELINE_LABELS, NOT_AVAILABLE } from "../../utils/messages";
import { StatusWithResubmission } from "../shared/status-with-resubmission";
import {
  CASE_RELATED_TEST_ID,
  CASE_RELATED_ROW_TEST_ID,
  CASE_RELATED_RESUBMISSION_TEST_ID,
} from "../../utils/case-history-test-ids";

interface CaseRelatedItemsProps {
  items: readonly ConciliationCaseRelatedItem[];
  currentItemId: number;
  onOpenCase: (itemId: number) => void;
}

export function CaseRelatedItems({
  items,
  currentItemId,
  onOpenCase,
}: CaseRelatedItemsProps): JSX.Element | null {
  if (items.length <= 1) return null;

  return (
    <section className="mt-4" data-testid={CASE_RELATED_TEST_ID}>
      <h3 className="mb-2 text-sm font-semibold text-[#613F4F]">
        {CASE_TIMELINE_LABELS.RELATED_TITLE}
      </h3>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{CASE_TIMELINE_LABELS.RELATED_COLUMNS.ID}</TableHead>
              <TableHead>{CASE_TIMELINE_LABELS.RELATED_COLUMNS.REGISTER}</TableHead>
              <TableHead>{CASE_TIMELINE_LABELS.RELATED_COLUMNS.DATE}</TableHead>
              <TableHead>{CASE_TIMELINE_LABELS.RELATED_COLUMNS.AMOUNT}</TableHead>
              <TableHead>{CASE_TIMELINE_LABELS.RELATED_COLUMNS.STATUS}</TableHead>
              <TableHead>{CASE_TIMELINE_LABELS.RELATED_COLUMNS.RELATION}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item: ConciliationCaseRelatedItem) => (
              <TableRow
                key={item.item_id}
                data-testid={CASE_RELATED_ROW_TEST_ID}
                className={cn(
                  item.item_id === currentItemId && "bg-gray-50 font-medium"
                )}
              >
                <TableCell>
                  <button
                    type="button"
                    className="underline text-[#613F4F]"
                    aria-label={CASE_TIMELINE_LABELS.RELATED_OPEN(item.item_id)}
                    onClick={(): void => onOpenCase(item.item_id)}
                  >
                    {item.item_id}
                  </button>
                </TableCell>
                <TableCell>{item.register_code ?? NOT_AVAILABLE}</TableCell>
                <TableCell>
                  {formatDate(item.conciliation_date) || NOT_AVAILABLE}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {formatAmount(item.amount)}
                </TableCell>
                <TableCell>
                  <StatusWithResubmission
                    status={item.status}
                    resubmitted={item.is_resubmitted}
                    resubmissionTestId={CASE_RELATED_RESUBMISSION_TEST_ID}
                  />
                </TableCell>
                <TableCell>
                  {CASE_TIMELINE_LABELS.RELATION[item.relation] ?? item.relation}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
